"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Bike, Check, MapPin, Timer } from "lucide-react";

export default function DeliveryFlowScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1280px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("How it works", "Cómo funciona")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Four steps. ", "Cuatro pasos. ")}
            <span className="text-redy-charcoal/30">{t("No driver to hire.", "Ningún repartidor que contratar.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "You don't run a fleet. You don't schedule anyone. Redy calls the courier when the food is ready — a courier from Uber Direct.",
              "No manejas una flota. No le haces horario a nadie. Redy llama al courier cuando la comida está lista — un courier de Uber Direct."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6">
          {/* Step 1 — customer orders on your own page */}
          <StepColumn
            n={1}
            delay={0.05}
            title={t("They order on your page.", "Ordenan en tu página.")}
            body={t(
              "Same site, same menu, your real prices. They type an address and Redy shows the live courier quote from Uber Direct before they pay.",
              "Mismo sitio, mismo menú, tus precios reales. Escriben una dirección y Redy muestra la cotización en vivo del courier de Uber Direct antes de pagar."
            )}
          >
            <PhoneFrame>
              <div className="bg-white h-full flex flex-col">
                <div className="h-7 bg-[#FAFAF8] flex items-center justify-between px-4 text-[8px] font-bold text-redy-charcoal">
                  <span>7:12</span>
                  <span>5G</span>
                </div>
                <div className="px-3 py-1.5 bg-[#F0EDE8]">
                  <div className="bg-white rounded-full px-3 py-1 text-[9px] text-redy-charcoal/60 truncate">
                    pepitos.useredy.com
                  </div>
                </div>
                <div className="px-3 py-3 bg-redy-charcoal text-white">
                  <div className="text-[11px] font-black">Pepitos WAAO</div>
                  <div className="text-[8px] text-white/60">{t("Delivery · 30–40 min", "Delivery · 30–40 min")}</div>
                </div>
                <div className="flex-1 px-3 py-2.5 bg-white">
                  <div className="text-[8px] font-black uppercase tracking-wider text-redy-secondary/60 mb-1.5">
                    {t("Deliver to", "Entregar en")}
                  </div>
                  <div className="flex items-center gap-1.5 border border-black/10 rounded-lg px-2 py-1.5 mb-3">
                    <MapPin className="w-2.5 h-2.5 shrink-0" style={{ color: "#D7263D" }} />
                    <span className="text-[8px] text-redy-charcoal truncate">1420 SW 8th St</span>
                  </div>
                  <div className="space-y-1.5 text-[9px]">
                    <div className="flex justify-between text-redy-charcoal">
                      <span>{t("Order", "Pedido")}</span>
                      <span className="tabular-nums font-bold">$45.00</span>
                    </div>
                    <div className="flex justify-between text-redy-secondary">
                      <span>{t("Delivery (3.2 mi)", "Envío (3.2 mi)")}</span>
                      <span className="tabular-nums">$7.99</span>
                    </div>
                  </div>
                  <div className="border-t border-dashed border-black/10 my-2" />
                  <div className="flex justify-between text-[10px] font-black text-redy-charcoal">
                    <span>{t("Total", "Total")}</span>
                    <span className="tabular-nums">$52.99</span>
                  </div>
                </div>
                <div
                  className="px-3 py-2 text-white text-center text-[9px] font-bold"
                  style={{ background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)" }}
                >
                  {t("Pay $52.99 →", "Pagar $52.99 →")}
                </div>
              </div>
            </PhoneFrame>
          </StepColumn>

          {/* Step 2 — kitchen marks it ready */}
          <StepColumn
            n={2}
            delay={0.18}
            title={t("The kitchen bumps it.", "La cocina la bumpea.")}
            body={t(
              "The ticket lands on the KDS like any other. When the cook marks it ready, Redy calls the courier. Nobody has to remember.",
              "El ticket cae en el KDS como cualquier otro. Cuando el cocinero la marca lista, Redy llama al courier. Nadie tiene que acordarse."
            )}
          >
            <KdsFrame>
              <div className="bg-[#0a1f1c] h-full p-3 text-white">
                <div className="flex justify-between items-center mb-3 pb-2 border-b border-white/10">
                  <div className="text-[9px] font-black uppercase tracking-wider text-white/40">
                    KDS · {t("Kitchen", "Cocina")}
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#F4722B" }} />
                    <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: "#F4722B" }}>
                      Live
                    </span>
                  </div>
                </div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: -10 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="rounded-[10px] p-2.5 mb-2"
                  style={{ backgroundColor: "rgba(244, 114, 43, 0.12)", border: "1px solid rgba(244, 114, 43, 0.4)" }}
                >
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="text-[10px] font-black text-white">#212</div>
                    <div
                      className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: "#F4722B", color: "white" }}
                    >
                      Delivery
                    </div>
                  </div>
                  <div className="space-y-0.5 text-[9px] text-white/85">
                    <div>1× Birria Plate</div>
                    <div>2× Quesabirria</div>
                    <div>1× Agua Fresca</div>
                  </div>
                  <div className="mt-2 pt-1.5 border-t border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-1 text-[8px] text-white/60">
                      <Timer className="w-2.5 h-2.5" />
                      <span>7:18 PM</span>
                    </div>
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 0.4, delay: 1.0 }}
                      className="flex items-center gap-1 text-[8px] font-black uppercase tracking-wider"
                      style={{ color: "#2ECC71" }}
                    >
                      <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                      {t("Ready", "Lista")}
                    </motion.div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.4, delay: 1.25 }}
                  className="rounded-[10px] p-2 bg-white/[0.04] border border-white/10"
                >
                  <div className="flex items-center gap-1.5 text-[8px] text-white/70">
                    <Bike className="w-2.5 h-2.5" style={{ color: "#F4722B" }} />
                    <span>{t("Courier requested…", "Courier solicitado…")}</span>
                  </div>
                </motion.div>
              </div>
            </KdsFrame>
          </StepColumn>

          {/* Step 3 — Uber courier picks it up */}
          <StepColumn
            n={3}
            delay={0.31}
            title={t("A courier from Uber Direct picks it up.", "Un courier de Uber Direct la recoge.")}
            body={t(
              "They walk in, take the bag, and leave. No hire, no car insurance, no scheduling.",
              "Entran, toman la bolsa y salen. Sin contratar, sin seguro de auto, sin horarios."
            )}
          >
            <PhoneFrame>
              <div className="bg-white h-full flex flex-col">
                <div className="h-7 bg-[#FAFAF8] flex items-center justify-between px-4 text-[8px] font-bold text-redy-charcoal">
                  <span>7:19</span>
                  <span>5G</span>
                </div>
                <div className="px-3 py-3 bg-redy-charcoal text-white">
                  <div className="text-[9px] font-black uppercase tracking-wider text-white/40 mb-1">
                    {t("Delivery #212", "Envío #212")}
                  </div>
                  <div className="text-[11px] font-black">{t("Courier assigned", "Courier asignado")}</div>
                </div>
                <div className="flex-1 px-3 py-3 space-y-2.5 bg-white">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: "rgba(244, 114, 43, 0.12)" }}
                    >
                      <Bike className="w-4 h-4" style={{ color: "#D7263D" }} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[9px] font-black text-redy-charcoal truncate">Marcos R.</div>
                      <div className="text-[8px] text-redy-secondary">{t("Uber courier · 4 min away", "Courier de Uber · a 4 min")}</div>
                    </div>
                  </div>

                  {[
                    { label: t("Order accepted", "Pedido aceptado"), done: true },
                    { label: t("Food ready", "Comida lista"), done: true },
                    { label: t("Courier en route", "Courier en camino"), done: false },
                    { label: t("Delivered", "Entregado"), done: false },
                  ].map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div
                        className="w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0"
                        style={{ backgroundColor: s.done ? "#2ECC71" : "rgba(0,0,0,0.07)" }}
                      >
                        {s.done && <Check className="w-2 h-2 text-white" strokeWidth={4} />}
                      </div>
                      <span
                        className={`text-[8px] font-bold ${
                          s.done ? "text-redy-charcoal" : "text-redy-secondary/50"
                        }`}
                      >
                        {s.label}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="px-3 py-2 bg-[#FAFAF8] border-t border-black/5">
                  <div className="flex justify-between text-[8px] font-bold">
                    <span className="text-redy-secondary">{t("Your cost", "Te cuesta")}</span>
                    <span className="tabular-nums" style={{ color: "#D7263D" }}>{t("from $2.99", "desde $2.99")}</span>
                  </div>
                </div>
              </div>
            </PhoneFrame>
          </StepColumn>

          {/* Step 4 — customer tracks it live */}
          <StepColumn
            n={4}
            delay={0.44}
            title={t("The customer tracks it live.", "El cliente la sigue en vivo.")}
            body={t(
              "A tracking page with your name on it shows each step and the courier's name, with a link to the live map. If the delivery gets cancelled, Redy charges you nothing for it.",
              "Una página de seguimiento con tu nombre muestra cada paso y el nombre del courier, con un link al mapa en vivo. Si el envío se cancela, Redy no te cobra nada por él."
            )}
          >
            <PhoneFrame>
              <div className="bg-white h-full flex flex-col">
                <div className="h-7 bg-[#FAFAF8] flex items-center justify-between px-4 text-[8px] font-bold text-redy-charcoal">
                  <span>7:31</span>
                  <span>5G</span>
                </div>
                <div className="px-3 py-1.5 bg-[#F0EDE8]">
                  <div className="bg-white rounded-full px-3 py-1 text-[9px] text-redy-charcoal/60 truncate">
                    pepitos.useredy.com/track
                  </div>
                </div>
                {/* Map mock */}
                <div className="relative flex-1 bg-[#EFEBE4] overflow-hidden">
                  <div className="absolute inset-0 opacity-40">
                    <div className="absolute left-0 right-0 top-[28%] h-[3px] bg-white" />
                    <div className="absolute left-0 right-0 top-[62%] h-[5px] bg-white" />
                    <div className="absolute top-0 bottom-0 left-[30%] w-[4px] bg-white" />
                    <div className="absolute top-0 bottom-0 left-[72%] w-[3px] bg-white" />
                  </div>
                  {/* Route */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 170 190" fill="none" aria-hidden="true">
                    <path
                      d="M30 150 L30 118 L122 118 L122 52"
                      stroke="#D7263D"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeDasharray="4 5"
                      opacity="0.5"
                    />
                  </svg>
                  {/* Restaurant pin */}
                  <div className="absolute left-[12%] top-[74%] w-3.5 h-3.5 rounded-full bg-redy-charcoal border-2 border-white shadow" />
                  {/* Courier pin */}
                  <motion.div
                    initial={{ left: "12%", top: "74%" }}
                    whileInView={{ left: "66%", top: "56%" }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 2.2, delay: 0.6, ease: "easeInOut" }}
                    className="absolute w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow-lg"
                    style={{ background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)" }}
                  >
                    <Bike className="w-3 h-3 text-white" />
                  </motion.div>
                  {/* Home pin */}
                  <div className="absolute left-[70%] top-[24%] flex flex-col items-center">
                    <MapPin className="w-4 h-4" style={{ color: "#1A1A1A" }} fill="#1A1A1A" />
                  </div>
                </div>
                <div className="px-3 py-2.5 bg-white border-t border-black/5">
                  <div className="text-[8px] font-black uppercase tracking-wider text-redy-secondary/60">
                    {t("Arriving", "Llega en")}
                  </div>
                  <div className="text-[14px] font-black text-redy-charcoal tabular-nums">
                    7:38 PM
                  </div>
                  <div className="text-[8px] text-redy-secondary">
                    {t("Marcos is 2 blocks away", "Marcos está a 2 cuadras")}
                  </div>
                </div>
              </div>
            </PhoneFrame>
          </StepColumn>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <div className="inline-flex items-center gap-2 bg-white px-5 py-3 rounded-full border border-black/5 shadow-sm">
            <Bike className="w-4 h-4" style={{ color: "#D7263D" }} />
            <span className="text-[13px] font-bold text-redy-charcoal">
              {t(
                "The customer, their phone number and the branding stay yours.",
                "El cliente, su teléfono y el branding siguen siendo tuyos."
              )}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function StepColumn({
  n,
  delay,
  title,
  body,
  children,
}: {
  n: number;
  delay: number;
  title: string;
  body: string;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
      className="flex flex-col items-center"
    >
      <div className="mb-6 relative">{children}</div>
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-black"
        style={{ backgroundColor: "#D7263D" }}
      >
        {n}
      </div>
      <div className="text-[17px] md:text-[19px] font-black tracking-tight text-redy-charcoal mt-4 mb-2 text-center">
        {title}
      </div>
      <p className="text-[13px] font-medium text-redy-secondary text-center max-w-[260px] leading-relaxed">
        {body}
      </p>
    </motion.div>
  );
}

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-[180px] h-[360px] bg-[#0a0a0a] rounded-[28px] p-[5px] shadow-[0_30px_60px_rgba(0,0,0,0.18)]">
      <div className="w-full h-full rounded-[24px] overflow-hidden bg-white relative">
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-16 h-3 bg-[#0a0a0a] rounded-full z-10" />
        {children}
      </div>
    </div>
  );
}

function KdsFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-[180px] h-[360px] bg-[#0a0a0a] rounded-[16px] p-[6px] shadow-[0_30px_60px_rgba(0,0,0,0.18)]">
      <div className="w-full h-full rounded-[10px] overflow-hidden bg-[#0a1f1c]">{children}</div>
    </div>
  );
}
